import React, { useState } from 'react';
import { 
  Paper, 
  Text, 
  NumberInput, 
  Button, 
  Group, 
  Stack, 
  Alert,
  Divider,
  Image,
  Tooltip,
  Box,
  Checkbox
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconCircleCheck, 
  IconCircleX, 
  IconInfoCircle, 
  IconShieldUp, 
  IconShieldOff,
  IconPaw
} from '@tabler/icons-react';
import { Effect, addEffect, removeEffect, EffectWithGroup } from '../api/effects';
import { Character } from '../api/characters';
import { notifications } from '@mantine/notifications';

interface EffectPropertiesProps {
  selectedEffect: EffectWithGroup | null;
  selectedCharacter: Character | null;
  serverConnected: boolean;
}

/**
 * Effect Properties component
 * Displays effect properties and allows applying/removing effects
 */
const EffectProperties: React.FC<EffectPropertiesProps> = ({ 
  selectedEffect, 
  selectedCharacter,
  serverConnected
}) => {
  // State for level and duration
  const [level, setLevel] = useState<number>(10);
  const [duration, setDuration] = useState<number>(60000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyToPet, setApplyToPet] = useState<boolean>(false);
  
  // Проверка: выбран ли group-эффект
  const isGroup = (selectedEffect as any)?.type === 'GROUP';

  // Check if buttons should be disabled
  const isButtonsDisabled = !serverConnected || !selectedEffect || !selectedCharacter || isSubmitting;

  // Get the icon URL for an effect
  const getEffectIconUrl = (iconFileName: string) => {
    return `/icons/effects/${iconFileName}.jpg`;
  };
  
  // Helper function to determine if an effect is beneficial based on is_harmful flag
  const isEffectBeneficial = (effect: Effect): boolean => {
    // Use comparison with "0" since is_harmful is a string
    return effect.is_harmful === "0";
  };
  
  // Handle applying effect
  const handleApplyEffect = async () => {
    if (isButtonsDisabled || !selectedEffect || !selectedCharacter) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (isGroup) {
        // Применить все эффекты из группы
        const group = selectedEffect as any;
        for (const eff of group.group_effects) {
          await addEffect({
            stateId: eff.state_id,
            level: eff.level,
            duration,
            characterId: selectedCharacter.id,
            characterName: selectedCharacter.name,
            applyToPet
          });
        }
        notifications.show({
          title: 'Group Applied',
          message: `Applied group ${group.name} to ${selectedCharacter.name}${applyToPet ? ' (pet)' : ''}`,
          color: 'green',
          icon: <IconCircleCheck size={16} />
        });
      } else {
        await addEffect({
          stateId: (selectedEffect as Effect).state_id,
          level,
          duration,
          characterId: selectedCharacter.id,
          characterName: selectedCharacter.name,
          applyToPet
        });
        
        notifications.show({
          title: 'Effect Applied',
          message: `Applied ${(selectedEffect as Effect).name} to ${selectedCharacter.name}${applyToPet ? ' (pet)' : ''}`,
          color: 'green',
          icon: <IconCircleCheck size={16} />
        });
      }
    } catch (err) {
      console.error('Failed to apply effect:', err);
      setError('Failed to apply effect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle removing effect
  const handleRemoveEffect = async () => {
    if (isButtonsDisabled || !selectedEffect || !selectedCharacter) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (isGroup) {
        const group = selectedEffect as any;
        for (const eff of group.group_effects) {
          await removeEffect({
            stateId: eff.state_id,
            level: eff.level,
            characterId: selectedCharacter.id,
            characterName: selectedCharacter.name
          });
        }
        notifications.show({
          title: 'Group Removed',
          message: `Removed group ${group.name} from ${selectedCharacter.name}`,
          color: 'yellow',
          icon: <IconCircleX size={16} />
        });
      } else {
        await removeEffect({
          stateId: (selectedEffect as Effect).state_id,
          level,
          characterId: selectedCharacter.id,
          characterName: selectedCharacter.name
        });
        
        notifications.show({
          title: 'Effect Removed',
          message: `Removed ${(selectedEffect as Effect).name} from ${selectedCharacter.name}`,
          color: 'yellow',
          icon: <IconCircleX size={16} />
        });
      }
    } catch (err) {
      console.error('Failed to remove effect:', err);
      setError('Failed to remove effect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Paper shadow="xs" p="md" radius={0} h="100%" withBorder className="h-full flex flex-col overflow-auto">
      <Text fw={700} mb="md">Properties</Text>
      
      {selectedEffect ? (
        <Stack gap="md" className="h-full">
          {/* Effect details */}
          <Group className="bg-gray-50 p-3 rounded-md">
            {/* Effect icon */}
            <div className="w-12 h-12 flex-shrink-0">
              <Image
                src={getEffectIconUrl(selectedEffect.icon_file_name)}
                alt={selectedEffect.name}
                fallbackSrc="/icons/fallback.jpg"
                fit="contain"
                className="rounded"
              />
            </div>
            
            {/* Effect name with buff/debuff icon */}
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <Text fw={700} size="md">
                  {selectedEffect.name}
                </Text>
                {/* Для группы показываем иконку Group */}
                {isGroup ? (
                  <Text size="xs" c="blue" fw={700} ml={8}>Group</Text>
                ) : ((() => {
                  console.log(`EffectProperties - Effect: ${(selectedEffect as Effect).name}, is_harmful: ${(selectedEffect as Effect).is_harmful}, isBeneficial: ${isEffectBeneficial(selectedEffect as Effect)}`);
                  return null;
                })())}
                {!isGroup && (isEffectBeneficial(selectedEffect as Effect) ? 
                  <IconShieldOff size={14} color="green" stroke={1.5} style={{ marginLeft: '4px' }} /> : 
                  <IconShieldUp size={14} color="red" stroke={1.5} style={{ marginLeft: '4px' }} />
                )}
              </div>
              
              {!isGroup && (
                <Text size="xs" c="dimmed" mt={4}>
                  State ID: {(selectedEffect as Effect).state_id}
                </Text>
              )}
            </div>
          </Group>
          
          <Divider />
          
          {/* Form container */}
          <div className="flex-grow">
            <Group grow align="flex-start" mb="md">
              {/* Level input */}
              {!isGroup && (
                <Tooltip 
                  label="Level determines the strength of the effect" 
                  position="top"
                  withArrow
                >
                  <Box>
                    <Text size="sm" mb={5} fw={500}>
                      Level <Text span color="red" component="span">*</Text>
                    </Text>
                    <NumberInput
                      value={level}
                      onChange={(value) => setLevel(Number(value))}
                      min={1}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                      hideControls={false}
                      required
                      size="sm"
                      placeholder="Count"
                    />
                    <Text size="xs" c="dimmed" mt={5}>
                      Effect level (strength)
                    </Text>
                  </Box>
                </Tooltip>
              )}
              
              {/* Duration input */}
              <Tooltip 
                label="Duration in seconds (up to 24 hours)" 
                position="top"
                withArrow
              >
                <Box>
                  <Text size="sm" mb={5} fw={500}>
                    Duration <Text span color="red" component="span">*</Text>
                  </Text>
                  <NumberInput
                    value={duration}
                    onChange={(value) => setDuration(Number(value))}
                    min={1}
                    stepHoldDelay={500}
                    stepHoldInterval={100}
                    hideControls={false}
                    required
                    size="sm"
                    placeholder="In seconds"
                  />
                  <Text size="xs" c="dimmed" mt={5}>
                    In seconds (max 24h)
                  </Text>
                </Box>
              </Tooltip>
            </Group>
            
            {/* Pet effect checkbox */}
            <Box mb="md">
              <Checkbox
                checked={applyToPet}
                onChange={(e) => setApplyToPet(e.currentTarget.checked)}
                label={
                  <Group gap={4} wrap="nowrap">
                    <IconPaw size={14} />
                    <Text size="sm">Apply state to pet</Text>
                  </Group>
                }
                description="Apply the effect to the character's pet instead of the character"
                size="sm"
              />
            </Box>
            
            {/* Selected character info */}
            {selectedCharacter && (
              <Group 
                className="bg-blue-50 p-2 rounded-md mb-4" 
                style={{ marginTop: '10px', marginBottom: '10px' }} 
                wrap="nowrap" 
                align="center"
              >
                <IconInfoCircle size={16} className="text-blue-500" />
                <Text size="xs" className="flex-grow">
                  Will be applied to: <Text span fw={600}>{selectedCharacter.name}{applyToPet ? ' (pet)' : ''}</Text>
                </Text>
              </Group>
            )}
          </div>
          
          {/* Error alert */}
          {error && (
            <Alert 
              icon={<IconAlertCircle size={16} />} 
              title="Error" 
              color="red" 
              variant="light"
              withCloseButton
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          {/* Action buttons */}
          <Group mt="xs">
            <Button 
              color={isEffectBeneficial(selectedEffect as Effect) ? "green" : "red"}
              onClick={handleApplyEffect} 
              disabled={isButtonsDisabled}
              loading={isSubmitting}
              fullWidth
              leftSection={<IconCircleCheck size={16} />}
              className={!isButtonsDisabled ? "shadow-sm hover:shadow-md transition-shadow" : ""}
            >
              Apply
            </Button>
            <Button 
              color="gray" 
              onClick={handleRemoveEffect} 
              disabled={isButtonsDisabled}
              loading={isSubmitting}
              fullWidth
              leftSection={<IconCircleX size={16} />}
              className={!isButtonsDisabled ? "shadow-sm hover:shadow-md transition-shadow" : ""}
            >
              Remove
            </Button>
          </Group>
          
          {/* Status indicators */}
          {!selectedCharacter && (
            <Alert 
              icon={<IconInfoCircle size={16} />} 
              color="blue" 
              variant="light"
              p="xs"
            >
              Select a character to apply/remove effects
            </Alert>
          )}
          
          {!serverConnected && (
            <Alert 
              icon={<IconAlertCircle size={16} />} 
              color="red" 
              variant="light"
              p="xs"
            >
              Server is offline
            </Alert>
          )}
        </Stack>
      ) : (
        <div className="flex items-center justify-center h-full">
          <Text c="dimmed" ta="center">
            Select an effect to view properties
          </Text>
        </div>
      )}
    </Paper>
  );
};

export default EffectProperties; 